/* --------------------------------------------------------------------------
   Sold out labelling

   Turns an add to cart button into a disabled "Sold out" button. Used when
   Shopify rejects a line because stock ran out after the page was rendered,
   so the answer appears on the button the customer just pressed rather than
   as an error inside the cart.

   Styling is applied inline to match the theme's own .kt-add-to-cart.kt-disabled
   rule, so this works for every button variant without needing a stylesheet
   that may not be loaded on the page.
   -------------------------------------------------------------------------- */

if (!window.KTMarkSoldOut) {
  window.KTMarkSoldOut = button => {
    if (!button) return;

    const prefix = window.KT_PREFIX || 'kt-';
    const label =
      (window.KROWN &&
        KROWN.settings &&
        KROWN.settings.locales &&
        KROWN.settings.locales.products_sold_out_variant) ||
      'Sold out';

    if (button.hasAttribute('data-kt-sold-out')) return;

    // Record what is being changed so the state can be undone when the
    // customer selects a different variant.
    button.setAttribute('data-kt-sold-out', '');
    button.setAttribute('data-kt-sold-out-label', button.getAttribute('title') || '');

    button.classList.remove(`${prefix}working`);
    button.classList.add(`${prefix}disabled`);
    button.disabled = true;
    button.setAttribute('aria-disabled', 'true');
    button.setAttribute('title', label);
    button.style.opacity = '0.36';
    button.style.pointerEvents = 'none';
    button.style.cursor = 'auto';

    const targets = button.querySelectorAll(
      '[data-js-product-add-to-cart-text], .' + prefix + 'button__text-value, .' +
        prefix + 'button__vertical-movement-text-value, .' + prefix +
        'quick-buy-button__text'
    );

    // Icon-only buttons have no label to change; the disabled state and the
    // title attribute carry the meaning there.
    targets.forEach(target => {
      target.setAttribute('data-kt-sold-out-text', target.textContent);
      target.textContent = label;
    });
  };
}

if (!window.KTClearSoldOut) {
  window.KTClearSoldOut = button => {
    if (!button || !button.hasAttribute('data-kt-sold-out')) return;

    const prefix = window.KT_PREFIX || 'kt-';
    const original = button.getAttribute('data-kt-sold-out-label') || '';

    button.removeAttribute('data-kt-sold-out');
    button.removeAttribute('data-kt-sold-out-label');
    button.classList.remove(`${prefix}disabled`);
    button.disabled = false;
    button.removeAttribute('aria-disabled');
    button.style.opacity = '';
    button.style.pointerEvents = '';
    button.style.cursor = '';
    if (original) button.setAttribute('title', original);

    button
      .querySelectorAll('[data-kt-sold-out-text]')
      .forEach(target => {
        target.textContent = target.getAttribute('data-kt-sold-out-text');
        target.removeAttribute('data-kt-sold-out-text');
      });
  };

  // A sold out button must not stay dead once the customer picks a different
  // variant. The theme announces every variant change, so clear the state for
  // the form that changed.
  document.addEventListener('krown:variant:change', event => {
    const form = event.detail && event.detail.productForm;
    const scope = form && form.closest ? form.closest('product-form') || form : null;
    if (!scope) return;
    scope
      .querySelectorAll('[data-kt-sold-out]')
      .forEach(button => window.KTClearSoldOut(button));
  });
}

/* ==========================================================================
   In-cart upsell

   <cart-upsell> keeps the merchant-controlled upsell in sync with the cart.

   The theme replaces the contents of #AjaxCartForm on every cart mutation, so
   this element watches that node and re-fetches its own section when it
   changes. That mirrors how the theme's existing <cart-recommendations>
   behaves, and means no edits to component-cart.js are needed.

   On the cart page the drawer itself is not rendered, so the cart drawer
   section renders the markup into a hidden holder instead and this element
   adopts it. That keeps a single source of configuration and avoids an extra
   request on page load.
   ========================================================================== */

if (typeof customElements.get('cart-upsell') === 'undefined') {
  class CartUpsell extends HTMLElement {
    constructor() {
      super();
      this._refreshing = false;
      this._debounceTimer = null;
    }

    connectedCallback() {
      this.adoptSourceMarkup();
      this._signature = this.cartSignature();
      this.observeCart();
    }

    disconnectedCallback() {
      if (this._observer) this._observer.disconnect();
      clearTimeout(this._debounceTimer);
    }

    /* --------------------------------------------------------------------
       Configuration
       -------------------------------------------------------------------- */

    get config() {
      const node = document.querySelector('[data-js-cart-upsell-config]');
      if (!node) return null;
      try {
        return JSON.parse(node.textContent);
      } catch (e) {
        return null;
      }
    }

    get sectionId() {
      if (this.dataset.section) return this.dataset.section;
      const config = this.config;
      return (config && config.sectionId) || null;
    }

    get hasOffers() {
      const config = this.config;
      return Boolean(config && config.hasOffers);
    }

    /* --------------------------------------------------------------------
       Cart page: adopt the markup rendered by the cart drawer section
       -------------------------------------------------------------------- */

    adoptSourceMarkup() {
      if (this.querySelector('[data-js-cart-upsell-rendered]')) return;

      const source = document.querySelector('[data-js-cart-upsell-source]');
      if (!source) return;

      const rendered = source.querySelector('[data-js-cart-upsell-rendered]');
      if (!rendered) return;

      this.innerHTML = rendered.outerHTML;
      source.remove();
    }

    /* --------------------------------------------------------------------
       Keeping in sync with the cart
       -------------------------------------------------------------------- */

    /* --------------------------------------------------------------------
       Which products are in the cart

       Offers are matched on the products in the cart, not their quantities.
       So a quantity change cannot change what is shown, and re-fetching on one
       would be a wasted request that also throws away any variant the customer
       had selected on a card. This signature lets us skip those.
       -------------------------------------------------------------------- */

    cartSignature() {
      const cartForm = document.getElementById('AjaxCartForm');
      if (!cartForm) return '';
      return Array.from(cartForm.querySelectorAll('[data-js-cart-item]'))
        .map(item => item.dataset.productId)
        .sort()
        .join(',');
    }

    observeCart() {
      // Nothing to keep in sync if the feature is switched off, or if no
      // offers have been configured yet — the cart drawer section only emits
      // hasOffers when at least one usable offer block exists.
      if (!this.sectionId || !this.hasOffers) return;

      const cartForm = document.getElementById('AjaxCartForm');
      if (!cartForm || !('MutationObserver' in window)) return;

      this._observer = new MutationObserver(() => {
        clearTimeout(this._debounceTimer);
        this._debounceTimer = setTimeout(() => {
          const signature = this.cartSignature();
          if (signature === this._signature) return;
          // Committed only once the refresh succeeds, so a failed request is
          // retried on the next cart change rather than leaving stale offers
          // on screen forever.
          this.refresh(signature);
        }, 250);
      });

      this._observer.observe(cartForm, {
        childList: true,
        subtree: true,
        attributes: false,
      });
    }

    refresh(signature) {
      const sectionId = this.sectionId;
      if (!sectionId) return;

      // A cart change while a refresh is in flight would otherwise be dropped
      // and never retried, leaving stale offers on screen.
      if (this._refreshing) {
        this._queued = true;
        return;
      }

      this._refreshing = true;
      this._queued = false;

      const base = window.location.pathname;
      const url = `${base}?section_id=${encodeURIComponent(sectionId)}`;

      // Give up rather than hold a stale view open indefinitely on a bad
      // connection.
      const controller =
        typeof AbortController === 'function' ? new AbortController() : null;
      const timer = controller
        ? setTimeout(() => controller.abort(), 8000)
        : null;

      fetch(url, controller ? { signal: controller.signal } : undefined)
        .then(response => {
          if (!response.ok) throw new Error('Cart upsell request failed');
          return response.text();
        })
        .then(text => {
          const parsed = new DOMParser().parseFromString(text, 'text/html');
          const rendered = parsed.querySelector('[data-js-cart-upsell-rendered]');
          this.innerHTML = rendered ? rendered.outerHTML : '';
          if (signature !== undefined) this._signature = signature;
        })
        .catch(() => {
          /* Leave the previous content in place, and leave the signature
             uncommitted so the next cart change retries. */
        })
        .finally(() => {
          if (timer) clearTimeout(timer);
          this._refreshing = false;
          if (this._queued) {
            this._queued = false;
            this.refresh(this.cartSignature());
          }
        });
    }

  }

  customElements.define('cart-upsell', CartUpsell);
}

/* --------------------------------------------------------------------------
   Inline variant selector

   Delegated at document level so it covers upsell cards wherever they appear —
   the cart drawer, the cart page, and the cross-sell popup — including cards
   injected after page load.
   -------------------------------------------------------------------------- */

if (!window.__ktUpsellVariantsBound) {
  window.__ktUpsellVariantsBound = true;

  document.addEventListener('change', event => {
    const select = event.target;
    if (!select || !select.matches || !select.matches('[data-js-upsell-variant-select]')) {
      return;
    }

    const card = select.closest('[data-js-upsell-card]');
    if (!card) return;

    const option = select.options[select.selectedIndex];
    if (!option) return;

    // The hidden input is what actually gets submitted, and what the theme's
    // ProductForm reads when it dispatches its cart events.
    const input = card.querySelector('[data-js-upsell-variant-input]');
    if (input) input.value = option.value;

    // Picking a different variant clears any sold out state on this card.
    if (window.KTClearSoldOut) {
      card
        .querySelectorAll('[data-kt-sold-out]')
        .forEach(button => window.KTClearSoldOut(button));
    }

    const regular = card.querySelector('[data-js-upsell-price-regular]');
    const compare = card.querySelector('[data-js-upsell-price-compare]');

    if (regular && option.dataset.price) {
      regular.textContent = option.dataset.price;
    }

    if (compare) {
      const comparePrice = (option.dataset.comparePrice || '').trim();
      if (comparePrice) {
        const inner = compare.querySelector('span') || compare;
        inner.textContent = comparePrice;
        compare.removeAttribute('hidden');
      } else {
        compare.setAttribute('hidden', '');
      }
    }
  });
}


/* --------------------------------------------------------------------------
   <upsell-add-to-cart>

   Handles the add for upsell cards directly rather than going through the
   theme's <product-form>. That element is built around a product page: it
   reads input[name="id"] itself and assumes a surrounding product component,
   which conflicts with the inline variant selector on these cards.

   After a successful add it calls window.refreshCart(false) — the theme's own
   documented hook for exactly this — so the cart line items, the subtotal and
   the header count all update the same way they do for any other add, without
   opening the drawer.
   -------------------------------------------------------------------------- */

/* --------------------------------------------------------------------------
   Upsell add to cart

   Handled by a delegated submit listener rather than a custom element. If a
   custom element fails to upgrade for any reason, its form falls through to a
   native POST to /cart/add, and the browser leaves the page for /cart — which
   is exactly the wrong outcome. A document level listener is attached as soon
   as this file runs, so that can't happen, and it covers cards injected into
   the popup or the drawer at any later point.

   After a successful add it calls window.refreshCart(false) — the theme's own
   documented hook — so the cart line items, the subtotal and the header count
   all update the same way they do for any other add, without opening the
   drawer over the popup.
   -------------------------------------------------------------------------- */

if (!window.__ktUpsellAddBound) {
  window.__ktUpsellAddBound = true;

  const addUrl = () => {
    const routes = (window.KROWN && KROWN.settings && KROWN.settings.routes) || {};
    const base =
      routes.cart_add_url ||
      `${(window.Shopify && Shopify.routes && Shopify.routes.root) || '/'}cart/add`;
    return `${base}.js`;
  };

  const clearError = host => {
    const existing = host.querySelector('[data-js-upsell-error]');
    if (existing) existing.remove();
  };

  const showError = (host, message) => {
    if (!message) return;
    const prefix = window.KT_PREFIX || 'kt-';
    const alert = document.createElement('span');
    alert.className = `${prefix}alert ${prefix}alert--error ${prefix}text-size--xxsmall`;
    alert.setAttribute('data-js-upsell-error', '');
    alert.innerHTML = message;
    host.appendChild(alert);
  };

  document.addEventListener(
    'submit',
    event => {
      const form = event.target;
      if (!form || typeof form.closest !== 'function') return;

      const host = form.closest('upsell-add-to-cart');
      if (!host) return;

      // Always stop the native submit, whatever happens next.
      event.preventDefault();
      if (host._working) return;

      const prefix = window.KT_PREFIX || 'kt-';
      const button = host.querySelector('[type="submit"]');
      const card = host.closest('[data-js-upsell-card]');

      host._working = true;
      if (button) button.classList.add(`${prefix}working`);
      clearError(host);

      fetch(addUrl(), {
        method: 'POST',
        body: new FormData(form),
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          Accept: 'application/javascript',
        },
      })
        .then(response => response.json())
        .then(data => {
          if (data && (data.status === 422 || data.status === 404)) {
            // Sold out: relabel the button rather than pushing a red error
            // block into a card that is only a few lines tall.
            if (window.KTMarkSoldOut) {
              window.KTMarkSoldOut(button);
            } else {
              showError(host, data.description || data.message);
            }
            return;
          }

          if (card) card.classList.add('kt-upsell-card--added');

          if (typeof window.refreshCart === 'function') {
            window.refreshCart(false);
          } else {
            refreshCartFallback();
          }

          const idField = form.querySelector('[name="id"]');
          const detail = {
            productForm: form,
            variantId: idField ? idField.value : null,
            source: host.dataset.upsellSource || 'cart',
            productHandle: card ? card.dataset.productHandle : null,
          };

          const dispatch =
            window.KROWN && KROWN.functions && KROWN.functions.eventDispatcher;
          if (dispatch) {
            // Fired for parity with the theme, so anything already listening
            // for adds keeps working.
            dispatch('krown:cart:add', detail);
            dispatch(
              detail.source === 'popup'
                ? 'krown:cross-sell:add'
                : 'krown:cart-upsell:add',
              detail
            );
          }
        })
        .catch(() => {
          showError(
            host,
            (window.KROWN &&
              KROWN.settings &&
              KROWN.settings.locales &&
              KROWN.settings.locales.cart_general_error) ||
              null
          );
        })
        .finally(() => {
          host._working = false;
          if (button) button.classList.remove(`${prefix}working`);
        });
    },
    true
  );

  // window.refreshCart comes from component-cart.js, which the theme only
  // loads when the cart is drawer driven.
  function refreshCartFallback() {
    fetch(`${window.location.pathname}?section_id=helper-cart`)
      .then(response => response.text())
      .then(text => {
        const parsed = new DOMParser().parseFromString(text, 'text/html');
        const form = document.getElementById('AjaxCartForm');
        const freshForm = parsed.getElementById('AjaxCartForm');
        if (form && freshForm) {
          form.innerHTML = freshForm.innerHTML;
          if (typeof form.ajaxifyCartItems === 'function') form.ajaxifyCartItems();
        }
        const subtotal = document.getElementById('AjaxCartSubtotal');
        const freshSubtotal = parsed.getElementById('AjaxCartSubtotal');
        if (subtotal && freshSubtotal) subtotal.innerHTML = freshSubtotal.innerHTML;
        const count = parsed.querySelector('[data-cart-count]');
        if (count) {
          document.querySelectorAll('[data-header-cart-count]').forEach(elm => {
            elm.textContent = count.textContent;
          });
        }
      })
      .catch(() => {});
  }
}
